/**
 * Ligas de los documentos de inscripción (foto, CURP y comprobante) en la
 * pantalla de Inscripciones del organizador.
 *
 * Esas ligas son FIRMADAS y vencen en una hora. Con la revisión de CURPs el
 * organizador se queda mucho rato en Inscripciones: sin renovarlas, el «Ver»
 * de la segunda hora abría «Esta liga ya venció». Al renovarlas solo se
 * cambia la liga de un archivo que sigue siendo EL MISMO: nada más de la
 * pantalla se toca (pagos, revisiones, lo que el organizador está editando),
 * y un archivo nuevo se verá con la siguiente carga normal.
 */

import type { RegistrationOrder } from "./api";

// La credencial también sale firmada (trae foto, nombre y fecha de nacimiento).
const DOCUMENT_LINK_KEYS = ["photo_url", "curp_pdf_url", "payment_receipt_url", "credential_url"] as const;

type DocumentLinkKey = (typeof DOCUMENT_LINK_KEYS)[number];

export type RegistrationDocumentKind = "photo" | "curp_pdf" | "payment_receipt";

type RegistrationDocumentsLike =
  | {
      photo_url?: string | null;
      curp_pdf_url?: string | null;
      payment_receipt_url?: string | null;
      has_photo?: boolean | null;
      has_curp_pdf?: boolean | null;
      has_payment_receipt?: boolean | null;
    }
  | null
  | undefined;

const LINK_KEY = { photo: "photo_url", curp_pdf: "curp_pdf_url", payment_receipt: "payment_receipt_url" } as const;
const RECEIVED_KEY = { photo: "has_photo", curp_pdf: "has_curp_pdf", payment_receipt: "has_payment_receipt" } as const;

/**
 * ¿Llegó el archivo? No basta con la liga: el perfil Capturista ve que la
 * familia ya mandó la CURP pero no la abre (la liga viene vacía). `has_*`
 * lo dice siempre; la liga sola cuenta para respuestas viejas sin `has_*`.
 */
export function registrationDocumentReceived(docs: RegistrationDocumentsLike, kind: RegistrationDocumentKind): boolean {
  if (!docs) return false;
  return Boolean(docs[RECEIVED_KEY[kind]] || docs[LINK_KEY[kind]]);
}

/** Las ligas de documentos privados que hoy muestra la pantalla. */
export function registrationDocumentLinks(orders: ReadonlyArray<RegistrationOrder>): Array<string | null | undefined> {
  const links: Array<string | null | undefined> = [];
  for (const order of orders) {
    const docs = order.participant?.registration_documents;
    if (!docs) continue;
    for (const key of DOCUMENT_LINK_KEYS) links.push(docs[key]);
  }
  return links;
}

/** Ruta del archivo sin la firma (`?exp=…&sig=…`); null si no se entiende. */
function fileOf(url: string): string | null {
  try {
    const base = typeof window !== "undefined" && window.location ? window.location.origin : "http://localhost";
    const parsed = new URL(url, base);
    return `${parsed.origin}${parsed.pathname}`;
  } catch {
    return null;
  }
}

/**
 * Copia de `current` con las ligas de `fresh` SOLO donde apuntan al mismo
 * archivo. Si nada cambió devuelve el mismo arreglo (React no repinta).
 */
export function renewRegistrationDocumentLinks(
  current: RegistrationOrder[],
  fresh: ReadonlyArray<RegistrationOrder>,
): RegistrationOrder[] {
  const freshById = new Map(fresh.map((order) => [order.id, order]));
  let changed = false;
  const next = current.map((order) => {
    const docs = order.participant?.registration_documents;
    const freshDocs = freshById.get(order.id)?.participant?.registration_documents;
    if (!order.participant || !docs || !freshDocs) return order;
    let renewed: typeof docs | null = null;
    for (const key of DOCUMENT_LINK_KEYS as ReadonlyArray<DocumentLinkKey>) {
      const before = docs[key];
      const after = freshDocs[key];
      if (!before || !after || before === after) continue;
      const file = fileOf(before);
      if (file === null || file !== fileOf(after)) continue;
      renewed = { ...(renewed ?? docs), [key]: after };
    }
    if (renewed === null) return order;
    changed = true;
    return { ...order, participant: { ...order.participant, registration_documents: renewed } };
  });
  return changed ? next : current;
}
